import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { BooleanOperation } from '~/engine/util/algorithms/sdf/SDFOperations';
import { OperationStep } from '~/engine/util/algorithms/sdf/boolean-operations/types';
import {
    getOperationLabel,
    MultiStructureSelect,
    OperationPicker,
    StructureOption,
} from './BooleanToolComponents';

interface AdvancedStepsListProps {
    steps: OperationStep[];
    expandedSteps: Set<string>;
    structureOptions: StructureOption[];
    getStructureColor: (name: string) => string;
    onToggleExpanded: (stepId: string) => void;
    onRemoveStep: (index: number) => void;
    onOperationChange: (stepIndex: number, operation: BooleanOperation) => void;
    onStructureChange: (stepIndex: number, values: string[]) => void;
    onAddStep: () => void;
}

export function AdvancedStepsList({
    steps,
    expandedSteps,
    structureOptions,
    getStructureColor,
    onToggleExpanded,
    onRemoveStep,
    onOperationChange,
    onStructureChange,
    onAddStep,
}: AdvancedStepsListProps) {
    // Skip first step (shown in main form)
    const additionalSteps = steps.slice(1);

    return (
        <Box
            sx={{
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 1,
            }}
        >
            {additionalSteps.map((step, idx) => (
                <AdvancedStep
                    key={step.id}
                    step={step}
                    isExpanded={expandedSteps.has(step.id)}
                    structureOptions={structureOptions}
                    getStructureColor={getStructureColor}
                    onToggleExpanded={() => onToggleExpanded(step.id)}
                    onRemove={() => onRemoveStep(idx + 1)}
                    onOperationChange={(op) => onOperationChange(idx + 1, op)}
                    onStructureChange={(vals) =>
                        onStructureChange(idx + 1, vals)
                    }
                />
            ))}

            <Button
                startIcon={<Plus size={12} />}
                onClick={onAddStep}
                variant="text"
                size="small"
                fullWidth
                sx={{
                    textTransform: 'none',
                    fontSize: 11,
                    py: 0.25,
                    justifyContent: 'flex-start',
                }}
            >
                Add step
            </Button>
        </Box>
    );
}

interface AdvancedStepProps {
    step: OperationStep;
    isExpanded: boolean;
    structureOptions: StructureOption[];
    getStructureColor: (name: string) => string;
    onToggleExpanded: () => void;
    onRemove: () => void;
    onOperationChange: (operation: BooleanOperation) => void;
    onStructureChange: (values: string[]) => void;
}

function AdvancedStep({
    step,
    isExpanded,
    structureOptions,
    getStructureColor,
    onToggleExpanded,
    onRemove,
    onOperationChange,
    onStructureChange,
}: AdvancedStepProps) {
    const selectedNames = step.rightOperands.map((r) => r.name);
    const summaryText =
        selectedNames.length === 0
            ? ''
            : selectedNames.length === 1
              ? selectedNames[0]
              : `${selectedNames.length} structures`;

    return (
        <Box sx={{ mb: 1 }}>
            {/* Step Header */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ cursor: 'pointer', py: 0.25 }}
                onClick={onToggleExpanded}
            >
                <Stack direction="row" alignItems="center" gap={0.5}>
                    {isExpanded ? (
                        <ChevronDown size={14} />
                    ) : (
                        <ChevronRight size={14} />
                    )}
                    <Typography
                        fontSize={11}
                        fontWeight={500}
                        color="primary.main"
                    >
                        Then {getOperationLabel(step.operation).toLowerCase()}
                    </Typography>
                    {!isExpanded && summaryText && (
                        <Typography fontSize={10} color="text.secondary">
                            {summaryText}
                        </Typography>
                    )}
                </Stack>
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    aria-label="Remove step"
                    sx={{ p: 0.25 }}
                >
                    <Trash2 size={12} />
                </IconButton>
            </Stack>

            {/* Step Content */}
            {isExpanded && (
                <Stack spacing={0.75} sx={{ mt: 0.5, pl: 2 }}>
                    <OperationPicker
                        value={step.operation}
                        onChange={onOperationChange}
                        size="small"
                    />

                    <MultiStructureSelect
                        label=""
                        values={selectedNames}
                        onChange={onStructureChange}
                        options={structureOptions}
                        getColor={getStructureColor}
                        placeholder="Select structure(s)..."
                    />
                </Stack>
            )}
        </Box>
    );
}
